import * as ProgramApi from 'api/program';
import { call, put, select, take } from 'redux-saga/effects';

import { EventCustomFeedbackCodes, EventValidationCodes } from 'defs/events';
import { EventFeedbackCodes, EventLevels, EventTypes } from 'defs/events';
import { DATA_BAG } from 'defs/offlineAvailability';
import Sequences from 'defs/sequences';

import { actionCreators as connectionAC, getIsOnline } from 'store/connection';
import { actionCreators as eventsAC } from 'store/events';
import { actionTypes as loginAT, getHasAuthenticationLoaded, getIsAuthenticated } from 'store/login';
import { actionCreators as offlineAvailabilityAC } from 'store/offlineAvailability';
import { actionCreators as profileAC, getIsPreviewUser, getIsProfileLoaded } from 'store/profile';
import { actionCreators as programPreviewAC } from 'store/programPreview';
import { actionCreators as programAC, getActiveProgram } from 'store/programs';
import { actionCreators as sequencesAC } from 'store/sequences';

import EventWrapper from 'models/global/EventWrapper';
export function* loadProgramPreview(action: any) {
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkFetchRequestError());
	}

	try {
		let isAuthenticated = yield select(getIsAuthenticated);
		const hasAuthenticationLoaded = yield select(getHasAuthenticationLoaded);
		let isProfileDataLoaded = yield select(getIsProfileLoaded);
		let isPreviewUser = false;

		if (!isAuthenticated && !hasAuthenticationLoaded) {
			yield take(loginAT.LOGIN_STATUS_KNOWN);
		}

		isAuthenticated = yield select(getIsAuthenticated);
		if (isAuthenticated) {
			isProfileDataLoaded = yield select(getIsProfileLoaded);
			if (!isProfileDataLoaded) {
				yield put(profileAC.doLoadUserData());
			}
		}

		isPreviewUser = yield select(getIsPreviewUser);

		if (isAuthenticated) {
			yield put(programAC.doRequestActiveProgramsLoading(true));

			const activePrograms = yield call(ProgramApi.fetchActivePrograms);

			if (activePrograms.length > 0) {
				yield put(programAC.doRequestActiveProgramsSuccess(activePrograms));
			}

			yield put(programAC.doRequestActiveProgramsLoading(false));
		}

		const data = yield call(ProgramApi.fetchProgramPreview, action.programId, isPreviewUser);

		yield put(
			offlineAvailabilityAC.doRegisterDataBag(DATA_BAG.PROGRAM_PREVIEW, {
				id: action.programId,
			})
		);

		yield put(programPreviewAC.doLoadPreviewSuccess(data));
	} catch (err) {
		yield put(programPreviewAC.doLoadPreviewFailure(err));
	}
}

export function* startProgram(action: any) {
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkSaveRequestError());
	}

	yield put(sequencesAC.doStartSequence(Sequences.PROGRAM_IS_STARTING));

	try {
		const isPreviewUser = yield select(getIsPreviewUser);
		const programTemplate = yield call(ProgramApi.fetchProgramTemplate, action.programId, isPreviewUser);
		let programStartData;

		if (isPreviewUser) {
			programStartData = yield call(ProgramApi.startProgramAsPreviewUser, programTemplate, action.date);
		} else {
			programStartData = yield call(ProgramApi.startProgram, programTemplate);
		}

		const model = new EventWrapper(programStartData);

		yield put(programPreviewAC.doStartProgramSuccess(model.getData()));
		yield put(eventsAC.doAddEvents(model.getEvents()));
	} catch (err) {
		let validationCode = EventFeedbackCodes.F05;

		/*
		 * Assert validation codes
		 * @see PULSWM - 2121
		 */
		switch (err.response.data.messageCode) {
			case 'error.validation.f07':
				validationCode = EventFeedbackCodes.F07;
				break;
			case 'error.validation.f08':
				validationCode = EventFeedbackCodes.F08;
				break;
			default:
				break;
		}

		yield put(
			eventsAC.doAddEvents([
				{
					eventLevel: EventLevels.NotAvailable,
					code: validationCode,
					eventType: EventTypes.Feedback,
					payload: { programId: action.programId },
				},
			] as any)
		);

		yield put(programPreviewAC.doStartProgramFailure(err));
	}

	yield put(sequencesAC.doFinishSequence(Sequences.PROGRAM_IS_STARTING));
}

export function* replaceProgram(action: Object): any {
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkSaveRequestError());
	}

	yield put(sequencesAC.doStartSequence(Sequences.PROGRAM_IS_STARTING));

	try {
		const activeProgram = yield select(getActiveProgram);
		if (!activeProgram) {
			yield startProgram(action);
			return;
		}
		yield call(ProgramApi.deleteProgram, activeProgram.id);
		yield startProgram(action);
	} catch (err) {
		if (err.response) {
			if (err.response.data.messageCode === EventValidationCodes.F08) {
				yield put(
					eventsAC.doAddEvents([
						{
							eventLevel: EventLevels.NotAvailable,
							code: EventFeedbackCodes.F08,
							eventType: EventTypes.Feedback,
						},
					] as any)
				);
			} else if (err.response.data.messageCode === EventValidationCodes.F07) {
				yield put(
					eventsAC.doAddEvents([
						{
							eventLevel: EventLevels.NotAvailable,
							code: EventFeedbackCodes.F07,
							eventType: EventTypes.Feedback,
						},
					] as any)
				);
			} else {
				yield put(
					eventsAC.doAddEvents([
						{
							eventLevel: EventLevels.NotAvailable,
							code: EventCustomFeedbackCodes.ValidationError,
							eventType: EventTypes.Feedback,
						},
					] as any)
				);
			}
		} else {
			yield put(
				eventsAC.doAddEvents([
					{
						eventLevel: EventLevels.NotAvailable,
						code: EventCustomFeedbackCodes.Error500,
						eventType: EventTypes.Feedback,
					},
				] as any)
			);
		}

		yield put(programPreviewAC.doReplaceProgramFailure(err));
	}

	yield put(sequencesAC.doFinishSequence(Sequences.PROGRAM_IS_STARTING));
}

export function* overwriteProgram(action: any): any {
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkSaveRequestError());
	}

	yield put(sequencesAC.doStartSequence(Sequences.PROGRAM_IS_STARTING));

	try {
		const isPreviewUser = yield select(getIsPreviewUser);
		const programTemplate = yield call(ProgramApi.fetchProgramTemplate, action.programId, isPreviewUser);
		let programOverwriteData;
		if (isPreviewUser) {
			programOverwriteData = yield call(ProgramApi.startProgramAsPreviewUser, programTemplate, action.date);
		} else {
			programOverwriteData = yield call(ProgramApi.overwriteProgram, programTemplate);
		}
		const model = new EventWrapper(programOverwriteData);

		yield put(programPreviewAC.doOverwriteProgramSuccess(model.getData()));
		yield put(eventsAC.doAddEvents(model.getEvents()));
	} catch (err) {
		if (err.response) {
			if (err.response.data.messageCode === EventValidationCodes.F08) {
				yield put(
					eventsAC.doAddEvents([
						{
							eventLevel: EventLevels.NotAvailable,
							code: EventFeedbackCodes.F08,
							eventType: EventTypes.Feedback,
						},
					] as any)
				);
			} else if (err.response.data.messageCode === EventValidationCodes.F07) {
				yield put(
					eventsAC.doAddEvents([
						{
							eventLevel: EventLevels.NotAvailable,
							code: EventFeedbackCodes.F07,
							eventType: EventTypes.Feedback,
						},
					] as any)
				);
			} else {
				yield put(
					eventsAC.doAddEvents([
						{
							eventLevel: EventLevels.NotAvailable,
							code: EventCustomFeedbackCodes.ValidationError,
							eventType: EventTypes.Feedback,
						},
					] as any)
				);
			}
		} else {
			yield put(
				eventsAC.doAddEvents([
					{
						eventLevel: EventLevels.NotAvailable,
						code: EventCustomFeedbackCodes.Error500,
						eventType: EventTypes.Feedback,
					},
				] as any)
			);
		}

		yield put(programPreviewAC.doOverwriteProgramFailure(err));
	}

	yield put(sequencesAC.doFinishSequence(Sequences.PROGRAM_IS_STARTING));
}
